import { pickBy, sumBy } from 'lodash';

import {
	CourseEntity,
	CourseOfferingEntity,
	PlanSelectionEntity,
	ProgramRuleCourse,
	ProgramRuleEntity,
	RequirementEntity,
	UnitConditionEntity,
} from 'Models/Entities';
import { DataIndex, reduceList } from 'Util/DataIndexUtils';
import { SearchAndFilterState } from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/CourseSelectionFilterInput';
import { courseMatchesSearchAndFilters } from 'Views/Pages/Frontend/StudyPlanWizard/SearchAndFilterUtilities';

export type RequirementNode = {
	// current requirement entity for this node
	requirement: RequirementEntity | ProgramRuleEntity;

	// calculated child nodes (subtrees) of the current node)
	childNodes: RequirementNode[];

	// offerings ids for a given course id, for this requirement
	offeringIdsByCourseId: DataIndex<string[]>;

	// min and max number of units for this node
	unitMinimum: number | undefined;
	unitCount: number;
	unitMaximum: number | undefined;

	// unique index of all courses selected in this subtree
	// inclusive of the current node
	subtreeSelectedCourses: DataIndex<CourseEntity>;
}

export const flattenIndexToIdArray = (offeringIndex: DataIndex<boolean>) => (
	Object.keys(offeringIndex)
		.filter(offeringId => offeringIndex[offeringId])
);

/**
 * Does a basic check to see if requirement
 * directly contains courses
 * @param req requirement entity to be checked
 * @returns boolean value if requirement contains courses
 */
const requirementDirectlyContainsCourses = (req: RequirementEntity | ProgramRuleEntity) => (
	req.category === 'COURSES'
);

export const getRequiredUnitsMinMax = (
	requirementRules: UnitConditionEntity[],
) => {
	const maxRule = requirementRules
		.find(rule => rule.ruleType === 'MAX');

	const minRule = requirementRules
		.find(rule => rule.ruleType === 'MIN');

	const equalRule = requirementRules
		.find(rule => rule.ruleType === 'EQUAL');

	if (equalRule && (minRule || maxRule)) {
		throw new Error('Invalid rules. Either Equal rule or Min/Max rules should be defined, not both');
	}

	// Set min units to 'min' rule if value is defined
	const requiredUnitsMin = minRule && minRule.units;
	// Set max units to 'max' rule if value is defined
	const requiredUnitsMax = maxRule && maxRule.units;

	/**
	 * if an equal rule is provided enforce both min and max to
	 * respect required 'equal' values.
	 *
	 * If not, return the optional min and max values
	 */
	return equalRule
		? [
			equalRule.units,
			equalRule.units,
		]
		: [
			requiredUnitsMin,
			requiredUnitsMax,
		];
};

export const checkRequirementRulesViolation = (requirementNode: RequirementNode) => {
	const { unitCount, unitMinimum, unitMaximum } = requirementNode;
	if (unitMaximum === undefined && unitMinimum === undefined) {
		return false;
	}

	const isBelowMinimum = unitMinimum !== undefined && (unitCount < unitMinimum);
	const isAboveMaximum = unitMaximum !== undefined && (unitCount > unitMaximum);

	return isBelowMinimum || isAboveMaximum;
};

/**
 * Creates a summation of either the courses selected in the
 * current requirement or child requirements. Course selections
 * must be unique.
 * @param currentRequirement
 * @param coursesInRequirement
 * @param isCourseSelected
 * @param childNodes
 * @returns index of child node courses entities
 */
const calculateSubtreeSelectedCourses = (
	currentRequirement: RequirementEntity | ProgramRuleEntity,
	coursesInRequirement: CourseEntity[],
	isCourseSelected: (requirementId: string, courseId: string) => boolean,
	childNodes: RequirementNode[],
): DataIndex<CourseEntity> => {
	if (requirementDirectlyContainsCourses(currentRequirement)) {
		const subtreeSelectedCourses: DataIndex<CourseEntity> = {};

		coursesInRequirement
			.filter(course => isCourseSelected(currentRequirement.id, course.id))
			.forEach(course => {
				subtreeSelectedCourses[course.id] = course;
			});

		return subtreeSelectedCourses;
	}

	return reduceList(
		childNodes,
		childNode => childNode.subtreeSelectedCourses,
	);
};

/**
 * Builds tree with required data for rendering Courses by their
 * Requirement
 *
 * @param courseOfferingIdByCourseIdByRequirementId
 * @param topLevelRequirements
 * @param requirementsByParentId
 * @param planSelections
 * @param coursesById
 * @param courseOfferingsById
 * @param searchAndFilterData
 * @returns
 */
export const buildRequirementTree = (
	courseOfferingIdByCourseIdByRequirementId: DataIndex<DataIndex<string[]>>,
	topLevelRequirements: RequirementEntity[] | ProgramRuleEntity[],
	requirementsByParentId: DataIndex<RequirementEntity[]> | DataIndex<ProgramRuleEntity[]>,
	planSelections: PlanSelectionEntity[],
	coursesById: DataIndex<CourseEntity>,
	courseOfferingsById: DataIndex<CourseOfferingEntity> | DataIndex<ProgramRuleCourse>, // todo typing needs refinement
	searchAndFilterData?: SearchAndFilterState,
) => {
	const tree: Record<string, RequirementNode> = {};

	const isCourseSelected = (requirementId: string, courseId: string) => (
		!!planSelections
			.find(planSelection => planSelection.courseId === courseId && planSelection.locationId === requirementId)
	);

	const mapRequirement = (currentRequirement: RequirementEntity | ProgramRuleEntity): RequirementNode => {
		const [unitMinimum, unitMaximum] = getRequiredUnitsMinMax(currentRequirement.unitConditions);

		const offeringsByCourse = (courseOfferingIdByCourseIdByRequirementId[currentRequirement.id] || {});

		const childNodes = requirementsByParentId[currentRequirement.id]
			? requirementsByParentId[currentRequirement.id].map(mapRequirement)
			: [];

		const coursesInRequirement = Object.keys(offeringsByCourse)
			.map(courseId => coursesById[courseId]);

		const subtreeSelectedCourses: DataIndex<CourseEntity> = calculateSubtreeSelectedCourses(
			currentRequirement,
			coursesInRequirement,
			isCourseSelected,
			childNodes,
		);

		const unitCount = sumBy(
			Object.values(subtreeSelectedCourses),
			course => course.units,
		);

		let filteredOfferingsByCourseId: DataIndex<string[]> = offeringsByCourse;

		if (searchAndFilterData) {
			filteredOfferingsByCourseId = pickBy(
				offeringsByCourse,
				offeringIdsForCourse => {
					// If course contains any offerings that matches the filter
					return offeringIdsForCourse
						.find(courseOfferingId => (
							courseMatchesSearchAndFilters(
								searchAndFilterData,
								courseOfferingsById[courseOfferingId],
							)
						));
				},
			);
		}

		return {
			requirement: currentRequirement,
			childNodes,
			unitMinimum,
			unitCount,
			unitMaximum,

			subtreeSelectedCourses,

			offeringIdsByCourseId: filteredOfferingsByCourseId,
		};
	};

	topLevelRequirements
		.forEach(requirement => {
			tree[requirement.id] = mapRequirement(requirement);
		});

	return tree;
};
